/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from "react";
import {
  Anchor,
  AspectRatio,
  Button,
  Modal,
  Rating,
  Tabs,
  Text,
} from "@mantine/core";
import {
  IconBook,
  IconCheck,
  IconDatabase,
  IconFileDollar,
  IconGift,
  IconPencil,
  IconSend,
  IconServerBolt,
  IconSettings,
  IconStack3,
  IconThumbUp,
  IconUsers,
} from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";

import "./landing.css";
import {
  authDetails,
  awsServices,
  bestPractices,
  blogDetails,
  databaseDetails,
  emailDetails,
  generalDetails,
  moreDetails,
  paymentDetails,
  servicesBuilt,
  techStack,
} from "./util.tsx";
import HeroShowcase from "./hero-showcase";

const { VITE_STAGE_DOMAIN } = import.meta.env;

const Hero = () => {
  // Control variable - set to false when you want to sell at full price
  const SHOW_COUNTDOWN = false;
  const [projectsModalOpened, setProjectsModalOpened] = useState(false);

  const handlePricingButtonClick = () => {
    const pricingSection = document.getElementById("pricing")!;
    pricingSection.scrollIntoView({ behavior: "smooth" });
  };

  const deadline = new Date("2025-06-06T00:00:00"); // Specify your deadline here
  const [remainingTime, setRemainingTime] = useState(calculateRemainingTime());

  const [lastUpdated, setLastUpdated] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 600); // Adjust the breakpoint as needed
  };

  useEffect(() => {
    handleResize(); // Check initial width
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // const [remainingTime, setRemainingTime] = useState("23:10:00");

  useEffect(() => {
    const fetchLastUpdatedDate = async () => {
      try {
        const response = await fetch(
          `https://api.${VITE_STAGE_DOMAIN}/v1/user/landing/last-updated`
        );

        const data = await response.json();
        setLastUpdated(data.lastUpdatedAt);
      } catch (error) {
        console.error("Error fetching last updated date:", error);
      }
    };

    fetchLastUpdatedDate();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(calculateRemainingTime());
    }, 1000);

    // Clear interval on component unmount to avoid memory leaks
    return () => clearInterval(interval);
  }, []);

  function calculateRemainingTime() {
    const now = new Date();
    const deadlineDate = new Date(deadline); // Convert deadline to a Date object
    let totalSeconds = Math.max(
      (deadlineDate.getTime() - now.getTime()) / 1000,
      0
    ); // Use getTime() to get the number of milliseconds since January 1, 1970

    const days = Math.floor(totalSeconds / (24 * 3600));
    totalSeconds %= 24 * 3600;
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return { days, hours, minutes, seconds };
  }

  const formatTime = (value: any, label: any) => {
    return (
      <div className="time-box">
        <div className="time-value">{value}</div>
        <div className="time-label">{label}</div>
      </div>
    );
  };

  const formatLastUpdated = () => {
    if (lastUpdated) {
      const formattedDate = formatDistanceToNow(new Date(lastUpdated), {
        addSuffix: true,
      });
      return formattedDate;
    }
    return "";
  };

  return (
    <section id="hero">
      <div className="hero-testimonials">
        {!isMobile ? (
          <>
            <div className="hero-testimonial-star">
              <Rating defaultValue={5} readOnly size={isMobile ? "xs" : "sm"} />
              <Text
                size={isMobile ? "sm" : "xl"}
                align="center"
                m="auto"
                mb="xl"
                maw={700}
              >
                "Great kit, easy to use"
              </Text>
            </div>
            <div className="hero-testimonial-star">
              <Rating defaultValue={5} readOnly size={isMobile ? "xs" : "sm"} />
              <Text
                size={isMobile ? "sm" : "xl"}
                align="center"
                m="auto"
                mb="xl"
                maw={700}
              >
                "A fantastic kit"
              </Text>
            </div>
            <div className="hero-testimonial-star">
              <Rating defaultValue={5} readOnly size={isMobile ? "xs" : "sm"} />
              <Text
                size={isMobile ? "sm" : "xl"}
                align="center"
                m="auto"
                mb="xl"
                maw={700}
              >
                "Saved me a lot of time"
              </Text>
            </div>
          </>
        ) : (
          <>
            <div className="hero-testimonial-star">
              <Rating defaultValue={5} readOnly size={isMobile ? "xs" : "sm"} />
              <Text
                size={isMobile ? "sm" : "xl"}
                align="center"
                m="auto"
                mb="xl"
                maw={700}
              >
                "Saved me a lot of time"
              </Text>
            </div>
          </>
        )}
      </div>
      <div className="hero-container">
        <div className="hero-container-a">
          <div className="hero">
            <h1 className="title">
              {/* Build your SaaS <br />
              <span
                style={{
                  textDecoration: "line-through",
                }}
              >
                in months
              </span>{" "}
              in days */}
              {/* Deploy your app in minutes with serverless architecture */}
              Frontend, Backend and Infra - all in one
            </h1>
            <h2 className="description ">
              Save time and money with the production-ready AWS serverless kit
              using{" "}
              <span className="hero-title-highlight-text"> best practices</span>
            </h2>

            <div
              className="button-container"
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: isMobile ? "0" : "0",
                width: "100%",
                maxWidth: isMobile ? "320px" : "none",
                margin: isMobile ? "0 auto" : "0",
              }}
            >
              <button
                className="btn-primary"
                onClick={handlePricingButtonClick}
                style={{
                  background: SHOW_COUNTDOWN
                    ? "linear-gradient(135deg, #ff6b6b, #ee5a24)"
                    : "linear-gradient(135deg, #00c9a7, #0891b2)",
                  border: "none",
                  borderRadius: "12px",
                  padding: isMobile ? "14px 24px" : "16px 32px",
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "600",
                  color: "white",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 20px rgba(0, 201, 167, 0.3)",
                  width: isMobile ? "100%" : "180px",
                  maxWidth: isMobile ? "280px" : "180px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: isMobile ? "12px" : "0",
                }}
                onMouseEnter={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.transform = "translateY(-2px)";
                  target.style.boxShadow = SHOW_COUNTDOWN
                    ? "0 6px 25px rgba(255, 107, 107, 0.4)"
                    : "0 6px 25px rgba(0, 201, 167, 0.4)";
                }}
                onMouseLeave={(e) => {
                  const target = e.target as HTMLButtonElement;
                  target.style.transform = "translateY(0)";
                  target.style.boxShadow = SHOW_COUNTDOWN
                    ? "0 4px 20px rgba(255, 107, 107, 0.3)"
                    : "0 4px 20px rgba(0, 201, 167, 0.3)";
                }}
              >
                {SHOW_COUNTDOWN ? "Buy now with 40% off" : "Buy now"}
              </button>
              <Button
                variant="outline"
                color="cyan"
                size={isMobile ? "md" : "lg"}
                onClick={() => {
                  const showcaseSection = document.querySelector(".hero-showcase-section");
                  showcaseSection?.scrollIntoView({ behavior: "smooth" });
                }}
                style={{
                  marginLeft: isMobile ? "0" : "1rem",
                  borderRadius: "12px",
                  padding: isMobile ? "14px 24px" : "16px 24px",
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "500",
                  borderWidth: "2px",
                  transition: "all 0.3s ease",
                  width: isMobile ? "100%" : "180px",
                  maxWidth: isMobile ? "280px" : "180px",
                }}
                sx={{
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 25px rgba(0, 201, 167, 0.2)",
                    backgroundColor: "rgba(0, 201, 167, 0.05)",
                  },
                }}
              >
                View Showcase
              </Button>
            </div>

            {SHOW_COUNTDOWN && (
              <div className="gift-box">
                <div className="offer-box">
                  <IconGift className="gift-icon" size="1.5rem" />{" "}
                  <span className="discount-100">40% off </span> until
                </div>
                {/* <span className="cooldown-time"> {remainingTime}</span>. */}
                <div className="cooldown-time">
                  {formatTime(remainingTime.days, "days")}{" "}
                  {formatTime(remainingTime.hours, "hours")}{" "}
                  {formatTime(remainingTime.minutes, "minutes")}{" "}
                  {formatTime(remainingTime.seconds, "seconds")}
                </div>
              </div>
            )}

            <div
              className="senja-embed hero-testimonial"
              data-id="f5ef400a-2abf-45b2-a885-16ee9abc2d4c"
              data-lazyload="false"
            ></div>

            <div className="last-update-box">
              {lastUpdated && (
                <p className="last-update">
                  Main branch updated: {formatLastUpdated()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="hero-container-b">
          <div className="hero-video">
            <AspectRatio ratio={16 / 9}>
              <iframe
                src="https://www.youtube.com/embed/2uh7gHW_hOs?si=g30j2wBB846_FUcG"
                title="Deploy your app in minutes with serverless architecture"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </AspectRatio>
          </div>
        </div>
      </div>

      <HeroShowcase isMobile={isMobile} />

      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h3
          style={{
            fontSize: isMobile ? "1.5rem" : "2rem",
            fontWeight: 700,
            marginBottom: "0.5rem",
            background: "linear-gradient(135deg, #00c9a7, #0891b2)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          What's Included
        </h3>
        <Text size={isMobile ? "sm" : "md"} color="dimmed">
          Everything you need to build and deploy your serverless application
        </Text>
      </div>

      <div className="hero-tabs">
        <Tabs color="cyan" variant="pills" radius="md" defaultValue="general">
          <Tabs.List>
            <Tabs.Tab value="general" icon={<IconPencil size="0.8rem" />}>
              General
            </Tabs.Tab>
            <Tabs.Tab value="tech-stack" icon={<IconStack3 size="0.8rem" />}>
              Tech Stack
            </Tabs.Tab>
            <Tabs.Tab value="payment" icon={<IconFileDollar size="0.8rem" />}>
              Payment
            </Tabs.Tab>
            <Tabs.Tab value="email" icon={<IconSend size="0.8rem" />}>
              Email
            </Tabs.Tab>
            <Tabs.Tab value="auth" icon={<IconUsers size="0.8rem" />}>
              Auth
            </Tabs.Tab>
            <Tabs.Tab value="blog" icon={<IconBook size="0.8rem" />}>
              Blog
            </Tabs.Tab>
            <Tabs.Tab value="database" icon={<IconDatabase size="0.8rem" />}>
              Database
            </Tabs.Tab>
            <Tabs.Tab
              value="best-practices"
              icon={<IconThumbUp size="0.8rem" />}
            >
              Best practices
            </Tabs.Tab>
            <Tabs.Tab
              value="aws-services"
              icon={<IconServerBolt size="0.8rem" />}
            >
              AWS Services
            </Tabs.Tab>
            <Tabs.Tab value="more" icon={<IconSettings size="0.8rem" />}>
              More
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general" pt="xs">
            <div className="check-lists">
              <div className="check-list" key="no-overhead">
                <IconCheck className="check-list-icon" />
                <p
                  className="check-list-text"
                  style={{
                    textDecoration: "underline",
                  }}
                >
                  No third-party services, no boilerplate headache, no hidden
                  costs,
                </p>
              </div>
              {generalDetails.map((gen) => (
                <div className="check-list" key={gen}>
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{gen}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="tech-stack" pt="xs">
            <div className="check-lists">
              {techStack.map((tech) => (
                <div className="check-list" key={tech}>
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{tech}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="payment" pt="xs">
            <div className="check-lists">
              {paymentDetails.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="blog" pt="xs">
            <div className="check-lists">
              {blogDetails.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="email" pt="xs">
            <div className="check-lists">
              {emailDetails.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="aws-services" pt="xs">
            <div className="check-lists">
              {awsServices.map((service) => (
                <div className="check-list" key={service}>
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{service}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="auth" pt="xs">
            <div className="check-lists">
              {authDetails.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="database" pt="xs">
            <div className="check-lists">
              {databaseDetails.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="best-practices" pt="xs">
            <div className="check-lists">
              {bestPractices.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="more" pt="xs">
            <div className="check-lists">
              {moreDetails.map((detail) => (
                <div className="check-list">
                  <IconCheck className="check-list-icon" />
                  <p className="check-list-text">{detail}</p>
                </div>
              ))}
            </div>
          </Tabs.Panel>
        </Tabs>
      </div>

      {/* Projects Modal */}
      <Modal
        opened={projectsModalOpened}
        onClose={() => setProjectsModalOpened(false)}
        title={
          <Text
            size={isMobile ? "lg" : "xl"}
            weight={700}
            color="#f8fafc"
            mb="xs"
          >
            Production-Ready Projects Showcase
          </Text>
        }
        size={isMobile ? "lg" : "xl"}
        padding={isMobile ? "md" : "xl"}
        radius="md"
        fullScreen={isMobile}
        sx={{
          ".mantine-Modal-inner": {
            paddingTop: isMobile ? "0" : "100px",
            paddingBottom: isMobile ? "0" : "40px",
            alignItems: isMobile ? "center" : "flex-start",
          },
          ".mantine-Modal-header": {
            padding: isMobile ? "20px 20px 16px 20px" : "24px 32px 16px 32px",
            marginBottom: 0,
          },
          ".mantine-Modal-body": {
            padding: isMobile ? "12px 20px 20px 20px" : "16px 32px 32px 32px",
          },
          ".mantine-Modal-close": {
            color: "#e2e8f0",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
        }}
      >
        <Text
          size="sm"
          color="#cbd5e1"
          mb={isMobile ? "md" : "xl"}
          sx={{ lineHeight: 1.6, opacity: 0.9 }}
        >
          The projects below are built using this serverless AWS kit. All
          infrastrucure and backend are built using this kit. The kit allows you
          to use any frontend framework you want.
        </Text>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fit, minmax(320px, 1fr))",
            gap: isMobile ? "1rem" : "1.5rem",
            marginBottom: isMobile ? "1rem" : "2rem",
            padding: isMobile ? "0" : "0.5rem",
          }}
        >
          {servicesBuilt.map(
            ({ label, href, highlight, description }, index) => {
              // Define background gradients for visual variety
              const backgroundGradients = [
                "linear-gradient(135deg, #f8fdfc 0%, #e6fffa 100%)", // Soft cyan
                "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)", // Soft blue
                "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)", // Soft gray
                "linear-gradient(135deg, #fefcbf 0%, #fef7cd 100%)", // Soft yellow
                "linear-gradient(135deg, #fef5e7 0%, #fed7aa 100%)", // Soft orange
                "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)", // Soft purple
              ];

              const currentBg =
                backgroundGradients[index % backgroundGradients.length];

              return (
                <Anchor
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "none",
                    display: "block",
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #e9ecef",
                      borderRadius: "12px",
                      padding: isMobile ? "1rem" : "1.5rem",
                      background: currentBg,
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: isMobile ? "auto" : "200px",
                      minHeight: isMobile ? "160px" : "200px",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      const target = e.currentTarget;
                      target.style.transform = "translateY(-2px)";
                      target.style.boxShadow =
                        "0 6px 20px rgba(0, 201, 167, 0.15)";
                      target.style.borderColor = "#00c9a7";
                    }}
                    onMouseLeave={(e) => {
                      const target = e.currentTarget;
                      target.style.transform = "translateY(0)";
                      target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                      target.style.borderColor = "#e9ecef";
                    }}
                  >
                    <div
                      style={{
                        flex: "0 0 auto",
                        marginBottom: isMobile ? "0.75rem" : "1rem",
                      }}
                    >
                      <Text
                        style={{
                          fontWeight: 600,
                          color: "#00c9a7",
                          fontSize: isMobile ? "1rem" : "1.1rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {label}
                      </Text>
                    </div>

                    <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
                      <div
                        style={{
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          lineHeight: 1.6,
                          color: "#4b5563",
                          display: "-webkit-box",
                          WebkitLineClamp: isMobile ? 4 : 6,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {description}
                      </div>
                    </div>

                    {/* Subtle gradient overlay for visual consistency */}
                    {!isMobile && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "40px",
                          background:
                            "linear-gradient(transparent, rgba(255, 255, 255, 0.9))",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                  </div>
                </Anchor>
              );
            }
          )}
        </div>
      </Modal>
    </section>
  );
};

export default Hero;
